import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ProductSwapTypes,
  PRODUCT_CHANGE_OPTIONS,
} from '../../utils/constants';
import {
  getCustomAttribute,
  getMinQuantity,
  itemIsFreeProduct,
} from '../utils';
import { IMenuOption } from '../../components/actionsMenu';
import useGlobalModalContext from '../../state/GlobalModalContext';
import { useGlobalContext } from './GlobalContext';
import {
  SC_LINE_MODS,
  SOURCE,
  scLineModAllowed,
} from '../../../lib/scLineMods';

export interface ProductSwapOptions {
  menuOptions: IMenuOption[];
  productSwapModal: ProductSwapTypes | null;
  openModal: (productType: ProductSwapTypes) => void;
  closeModal: () => void;
}

export const useProductSwapInitializer = (
  product: Product,
  removeProduct: (lineId: string) => void,
  setShowEditMyBoxModal: (showEditMyBoxModal: boolean) => void,
  setShowCurateShuffleModal: (showCurateShuffleModal: boolean) => void,
  openSlidePanel: (
    type: ProductSwapTypes,
    productId: string,
    variantId: string,
  ) => void,
  closeSlidePanel: () => void,
  isLastLine?: boolean,
): ProductSwapOptions => {
  const { t } = useTranslation();
  const { openGlobalModal, closeGlobalModal } = useGlobalModalContext();
  const {
    onDrawerClose,
    customerPortalSettings,
    enableShuffleUpcomingProducts,
  } = useGlobalContext();
  const isFreeProduct =
    itemIsFreeProduct(product, customerPortalSettings?.preventEditFreeItems) &&
    product.PrepaidVersion !== 3;

  const shouldHideChangeProductOptions =
    customerPortalSettings?.productChange ===
      PRODUCT_CHANGE_OPTIONS.NOT_ALLOWED ||
    isFreeProduct ||
    !scLineModAllowed(product, SC_LINE_MODS.SWAP_VARIANT, SOURCE.dynamo);

  const isPrepaidV2 = !!product.PrepaidV2;

  const isRemovable = scLineModAllowed(
    product,
    SC_LINE_MODS.REMOVAL,
    SOURCE.dynamo,
  );
  const isEditable = scLineModAllowed(
    product,
    SC_LINE_MODS.EDIT_BUNDLE,
    SOURCE.dynamo,
  );

  const isOneTimeAddon = !!product.OneTimeAddon;
  const hasMinQuantityAttribute = getMinQuantity(product);
  const isAddon = getCustomAttribute('_addon', product.CustomAttributes);
  const isShuffle =
    getCustomAttribute('_bundleType', product.CustomAttributes) === 'shuffle';
  const isClassicBundle =
    getCustomAttribute('_bundleType', product.CustomAttributes) === 'classic';
  const bundleProductId = getCustomAttribute(
    '_bundleProductId',
    product.CustomAttributes,
  );

  let menuOptions: IMenuOption[] = [];

  if (
    (!hasMinQuantityAttribute && !isPrepaidV2 && !isLastLine && isRemovable) ||
    isOneTimeAddon
  ) {
    menuOptions.push({
      text: t('titles.remove'),
      productVariantId: product.VariantId,
      type: 'remove',
      color: '#E53E3E',
      action: () => {
        removeProduct(product.SubscriptionContractLineId);
        onDrawerClose();
      },
    });
  }

  if (!shouldHideChangeProductOptions) {
    menuOptions.unshift({
      text: t('subscription.update_variant'),
      productVariantId: product.VariantId,
      type: 'update_variant',
      action: () => {
        onDrawerClose();
        const shouldBypassVariantSwap =
          (window?.bundleapp?.settings?.bypassVariantSwap ||
            customerPortalSettings?.skipVariantSection) &&
          !isPrepaidV2;

        if (shouldBypassVariantSwap) {
          openModal(ProductSwapTypes.PRODUCT);
        } else {
          openSlidePanel(
            ProductSwapTypes.VARIANT,
            product.ProductId,
            product.VariantId,
          );
        }
      },
    });
  }

  if (bundleProductId && isShuffle && enableShuffleUpcomingProducts) {
    menuOptions.unshift({
      productVariantId: product.VariantId,
      text: t('tables.curate_upcoming'),
      type: 'curate_upcoming',
      action: () => {
        setShowCurateShuffleModal(true);
      },
    });
  }

  if (
    bundleProductId &&
    !isAddon &&
    !isShuffle &&
    !isClassicBundle &&
    !isFreeProduct
  ) {
    if (isEditable) {
      menuOptions.unshift({
        productVariantId: product.VariantId,
        text: t('bundles.edit_my_box'),
        type: 'edit_my_box',
        action: () => {
          openModal(ProductSwapTypes.BUNDLE);
          onDrawerClose();
        },
      });
    }

    // Remove the option to edit a variant if it's a bundle
    menuOptions = menuOptions.filter(
      ({ text }) => text !== t('subscription.update_variant'),
    );
  }

  const openModal = (productType: ProductSwapTypes) => {
    closeSlidePanel();
    setProductSwapModal(productType);
    openGlobalModal();
  };

  const closeModal = () => {
    setShowEditMyBoxModal(false);
    setProductSwapModal(null);
    closeGlobalModal();
  };

  const [productSwapModal, setProductSwapModal] =
    useState<ProductSwapTypes | null>(null);

  return {
    menuOptions,
    productSwapModal,
    openModal,
    closeModal,
  };
};
